import type { LocalChannel } from '../../db/db'
import { channelDisplayName, lastActivity, type UserMap } from '../../lib/channels'
import { quietState } from '../channels/quiet'

export type ConversationFilter = 'all' | 'unread' | 'channels' | 'groups'

export interface FilterOptions {
  filter: ConversationFilter
  query: string
  showArchived: boolean
  /** Show only the conversations that will not notify me, so a mute made by mistake is easy to find. */
  onlyQuiet?: boolean
  users: UserMap
  meId: string | null
}

export interface ConversationRowData {
  channel: LocalChannel
  name: string
}

/** Member channels for the list, filtered and newest first. */
export function filterChannels(channels: LocalChannel[], options: FilterOptions): ConversationRowData[] {
  const query = options.query.trim().toLowerCase()
  return channels
    .filter((c) => c.isMember)
    .filter((c) => options.showArchived || !c.archivedAt)
    .filter((c) => !options.onlyQuiet || Boolean(quietState(c)))
    .filter((c) => {
      switch (options.filter) {
        case 'unread':
          return c.unread > 0
        case 'channels':
          return c.type === 'channel'
        case 'groups':
          return c.type === 'group_dm'
        default:
          return true
      }
    })
    .map((channel) => ({ channel, name: channelDisplayName(channel, options.users, options.meId) }))
    .filter((row) => !query || row.name.toLowerCase().includes(query))
    // Newest first, except in Unread: there the conversations that will not notify you wait under the ones that will.
    .sort((a, b) => {
      if (options.filter === 'unread') {
        const quiet = Number(Boolean(quietState(a.channel))) - Number(Boolean(quietState(b.channel)))
        if (quiet !== 0) return quiet
      }
      return lastActivity(a.channel) < lastActivity(b.channel) ? 1 : -1
    })
}

/** The number the app shows, counted as the phone's own icon counts it: what a muted conversation holds is not in it. */
/** How many conversations will not notify me at all. */
export function quietCount(channels: LocalChannel[]): number {
  return channels.filter((c) => c.isMember && !c.archivedAt && quietState(c)).length
}

export function totalUnread(channels: LocalChannel[]): number {
  return channels.filter((c) => c.isMember && !quietState(c)).reduce((sum, c) => sum + c.unread, 0)
}
